/**
 * Export bugs found — filtered, as a CSV a developer can open and work from.
 *
 * One row per failing test (or every test, if asked for "all"), across however
 * many finished runs match the filters — not one run at a time. "What's broken in
 * CAIT right now" is a question about many runs, and opening them one by one to
 * answer it is exactly the tedium this exists to remove.
 *
 * The export itself is a plain `<a href>` to a server route that streams CSV with
 * a `content-disposition: attachment` — the same pattern `reportUrl`/`screenshotUrl`
 * already use for anything the server serves as a file rather than JSON. No fetch,
 * no blob handling: the browser's own download machinery does the rest.
 */
import { useState } from 'react';
import { api, type AppDecl, type EnvironmentName } from '../api';

export function ExportModal({
  applications,
  onClose,
}: {
  applications: AppDecl[];
  onClose: () => void;
}) {
  const [environment, setEnvironment] = useState<EnvironmentName | 'all'>('all');
  const [scope, setScope] = useState('all');
  const [status, setStatus] = useState<'failed' | 'all'>('failed');

  const url = api.exportUrl({ environment, scope, status });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-6"
      style={{ background: 'hsl(var(--bg) / 0.9)' }}
      data-testid="export-modal"
    >
      <div className="surface my-8 w-full max-w-md p-0">
        <div className="flex items-baseline gap-3 p-4" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 className="text-base font-semibold">Export bugs found</h2>
          <button className="btn btn-ghost ml-auto text-xs" onClick={onClose} data-testid="export-modal-close">
            Close
          </button>
        </div>

        <div className="flex flex-col gap-4 p-5">
          <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
            One row per test, across every finished run matching these filters — a CSV
            with the test name, file, error and which run it came from, so a developer
            can open it and start fixing rather than clicking through runs one at a time.
          </p>

          <label className="flex flex-col gap-1 text-sm">
            <span className="label">Environment</span>
            <select
              className="field"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as EnvironmentName | 'all')}
              data-testid="export-environment"
            >
              <option value="all">All environments</option>
              <option value="dev">dev</option>
              <option value="qa">qa</option>
              <option value="prod">prod</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="label">Application</span>
            <select
              className="field"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="export-scope"
            >
              <option value="all">All applications</option>
              {applications
                .filter((a) => a.scope !== 'all')
                .map((a) => (
                  <option key={a.scope} value={a.scope}>{a.label}</option>
                ))}
            </select>
          </label>

          <fieldset className="flex flex-col gap-1.5 text-sm">
            <span className="label">Which tests</span>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="export-status"
                checked={status === 'failed'}
                onChange={() => setStatus('failed')}
                data-testid="export-status-failed"
              />
              Only failing (and timed-out) tests — the bugs
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="export-status"
                checked={status === 'all'}
                onChange={() => setStatus('all')}
                data-testid="export-status-all"
              />
              Every test, passing and failing
            </label>
          </fieldset>

          <a
            href={url}
            className="btn btn-primary text-center"
            data-testid="export-download"
            onClick={() => setTimeout(onClose, 150)}
          >
            Export CSV
          </a>
        </div>
      </div>
    </div>
  );
}
